import { Link } from 'react-router-dom'

import { useCurrentUser } from '@/api/hooks'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { STAFF_ROLE_LABELS, STAFF_ROLE_ORDER, type StaffRole } from '@/data/staff-roles'

function roleLabel(role: string) {
  return STAFF_ROLE_LABELS[role as StaffRole]?.singular ?? role
}

/** What the signed-in account is, which shows it can work in, and how to keep it secure. */
export function MyAccountPage() {
  const { data: user, isLoading, isError } = useCurrentUser()

  if (!user && isError)
    return (
      <p role="alert" className="text-sm text-destructive">
        Your account could not be loaded. Check the connection and refresh the page.
      </p>
    )
  if (isLoading || !user) return <Skeleton className="h-40 w-full" />

  // The roles arrive with the show names, from the same read as the account itself.
  const shows = new Map<string, { name?: string; roles: string[] }>()
  for (const { showId, role, showName } of user.showRoles ?? []) {
    const show = shows.get(showId) ?? { name: showName, roles: [] }
    show.roles.push(role)
    shows.set(showId, show)
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6" data-testid="my-account-page">
      <h1 className="text-2xl font-semibold tracking-tight">My account</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            Signed in as <span className="font-medium break-all">{user.email}</span>
            {user.isAdmin ? (
              <Badge variant="secondary" className="ml-2">
                Administrator
              </Badge>
            ) : null}
          </p>
          <p>
            {user.person
              ? `Linked to ${user.person.name}; your show roles are this person's staff assignments.`
              : 'Not linked to a person, so you have no show roles. Ask an administrator to link your account.'}
          </p>
          <p className="text-muted-foreground">
            To change your sign-in email, ask an administrator.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Show access</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {user.isAdmin ? (
            <p>As an administrator you can open and manage every show.</p>
          ) : shows.size === 0 ? (
            <p>You are not assigned to any show yet. Ask a show organizer to add you as staff.</p>
          ) : (
            <ul className="space-y-1">
              {[...shows].map(([showId, { name, roles }]) => (
                <li key={showId}>
                  <span className="font-medium">{name ?? 'A show'}</span>:{' '}
                  {[...roles]
                    .sort(
                      (a, b) =>
                        STAFF_ROLE_ORDER.indexOf(a as StaffRole) -
                        STAFF_ROLE_ORDER.indexOf(b as StaffRole)
                    )
                    .map(roleLabel)
                    .join(', ')}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Password and sessions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <Link to="/account/change-password" className="underline">
              Change your password
            </Link>
            . This signs you out on every other device.
          </p>
          <p>
            <strong>Log out</strong> in the account menu signs you out on every device, not just
            this one. If you forget your password, ask an administrator for a reset link.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default MyAccountPage
